import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import { Button } from "../../ui/Button";
import ContentMarkdown, { noCitations } from "../../ContentMarkdown";
import { GPT_COMPOSER_INPUT_ID } from "./GptConversation";
import { useNotificationOptIn } from "../../../domains/notification/hooks/useNotificationOptIn";
import { useNotificationPermissionState } from "../../../domains/notification/hooks/useNotificationPermissionState";
import { GPT_ANSWER_CLASS } from "../../../domains/gpt/answer";
import { gaEventPushNotificationsDone } from "../../../tools/analytics/domains/onboarding.analytics";

// The getting-started actions shown inside an onboarding JuisciGPT
// conversation (B2C users land on one right after onboarding, in place of the
// scripted profiling conversation). "Customize my feed" is where B2C users
// meet the push-permission ask — they never saw the onboarding prompt — then
// the preference conversation takes over the page (no modal).
type GettingStartedStep = { status: "actions" } | { status: "notifications" };

export function OnboardingGettingStarted({
  onCustomize,
}: {
  // Opens the preference conversation in place of the chat (owned by the
  // page — it swaps its content, so no modal is involved).
  onCustomize: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const [step, setStep] = useState<GettingStartedStep>({ status: "actions" });
  const { permissionState } = useNotificationPermissionState();

  const notif = useNotificationOptIn(() => {
    gaEventPushNotificationsDone();
    onCustomize();
  });

  function customizeMyFeed(): void {
    // The push ask comes first, at the moment the user personalizes their
    // feed (B2C onboarding no longer has a notification step). While the
    // permission state is still loading (null), default to showing the ask so
    // it can't be raced past — same default as the onboarding step.
    if (
      permissionState === null ||
      permissionState.status === "notDetermined"
    ) {
      setStep({ status: "notifications" });
      return;
    }
    onCustomize();
  }

  function focusComposer(): void {
    const composer = document.getElementById(GPT_COMPOSER_INPUT_ID);
    if (composer === null) return;
    composer.focus();
  }

  return (
    <div className="px-5">
      {step.status === "notifications" ? (
        <div className="flex flex-col gap-4">
          <ContentMarkdown
            className={GPT_ANSWER_CLASS}
            math={false}
            linkVariant="tag"
            streaming={false}
            citation={noCitations}
          >
            {notif.showOpenSettings
              ? t(
                  "**Stay current with science.** Notifications are currently disabled. Open settings to turn them back on."
                )
              : t(
                  "**Stay current with science.** I'll notify you only when impactful articles in your field are published."
                )}
          </ContentMarkdown>
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={notif.isRequesting}
              onClick={() => {
                if (notif.showOpenSettings) notif.openSettings();
                else notif.enable();
              }}
            >
              {notif.isRequesting ? (
                <IconLoader2 size={16} className="animate-spin" />
              ) : notif.showOpenSettings ? (
                t("Open settings")
              ) : (
                t("Turn on Notifications")
              )}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              disabled={notif.isRequesting}
              onClick={() => {
                notif.maybeLater();
              }}
            >
              {t("Maybe later")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ActionCard
            label={t("Ask a medical question")}
            onClick={focusComposer}
          />
          <ActionCard
            label={t("Customize my feed")}
            onClick={customizeMyFeed}
          />
          <ActionCard
            label={t("Search publications")}
            onClick={() => {
              history.push("/search");
            }}
          />
          <ActionCard
            label={t("Explore my feed")}
            onClick={() => {
              history.push("/discovery");
            }}
          />
        </div>
      )}
    </div>
  );
}

// One getting-started action, styled like the JuisciGPT landing's template
// question cards.
function ActionCard({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      className="bg-white w-full px-6 py-4 rounded-3xl shadow-[0px_4px_10px_rgba(0,0,0,0.05),0px_4px_20px_rgba(0,0,0,0.05)] border-none cursor-pointer"
      onClick={onClick}
    >
      <p className="font-dm-sans font-normal text-[13px] leading-[1.6] tracking-[-0.15px] text-[#004852] text-left m-0">
        {label}
      </p>
    </button>
  );
}
